/**
 * Secure Storage Utility
 * Token expires in 16 hours (auto-refreshed at 15h), Login session expires in 24 hours
 */
//CONSTANTS 

const STORAGE_KEYS = {
  TOKEN: "auth_token",
  REFRESH_TOKEN: "auth_refresh_token",
  ROLE: "auth_role",
  ENTITY_TYPE: "auth_entity_type",
  PERMISSIONS: "auth_permissions",
  TOKEN_EXPIRY: "auth_token_expiry",
  USER_DATA: "auth_user_data",
  LOGIN_SESSION: "auth_login_session",
};
// Token expires in 16 hours (57600 seconds), refresh 1 hour before expiry (at 15h mark)
const TOKEN_REFRESH_BUFFER = 60 * 60 * 1000;
// Login session expires in 24 hours
const LOGIN_SESSION_DURATION = 24 * 60 * 60 * 1000;

//  TOKEN MANAGEMENT 
/**
 * Store JWT token securely in localStorage
 * @param {string} token - JWT token
 * @param {number} expiresIn - Token expiry time in seconds (default 3600)
 */
export const setToken = (token, expiresIn = 57600) => {
  if (!token) return;

  try {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);

    // Calculate and store token expiry time (16 hours)
    const tokenExpiryTime = Date.now() + (expiresIn * 1000);
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, String(tokenExpiryTime));
    // Set login session expiry (24 hours) if not already set
    const existingSession = localStorage.getItem(STORAGE_KEYS.LOGIN_SESSION);
    if (!existingSession) {
      const loginSessionExpiry = Date.now() + LOGIN_SESSION_DURATION;
      localStorage.setItem(STORAGE_KEYS.LOGIN_SESSION, String(loginSessionExpiry));
    }
  } catch (error) {
    console.error("Error storing token:", error);
  }
};

/**
 * Get token from localStorage
 * Returns null if token is expired OR login session (24h) has expired
 */
export const getToken = () => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const tokenExpiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
    const loginSession = localStorage.getItem(STORAGE_KEYS.LOGIN_SESSION);

    if (!token) return null;

    // Check if login session (24 hours) has expired
    if (loginSession && Date.now() > Number.parseInt(loginSession)) {
      console.warn("[SecureStorage] Login session (24h) expired");
      clearAuth(); // Clear all auth data
      return null;
    }

    // Check if token (16 hours) is expired - will trigger refresh
    if (tokenExpiry && Date.now() > Number.parseInt(tokenExpiry)) {
      console.warn("[SecureStorage] Token expired, needs refresh");
      return null;
    }

    return token;
  } catch (error) {
    console.error("Error getting token:", error);
    return null;
  }
};

/**
 * Get raw token without expiry check (for refresh flow)
 * This allows getting the token even if it's expired, for refresh purposes
 */
export const getRawToken = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  } catch (error) {
    console.error("Error getting raw token:", error);
    return null;
  }
};

/**
 * Check if token needs refresh (within 5 minutes of expiry)
 */
export const shouldRefreshToken = () => {
  try {
    const tokenExpiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
    const loginSession = localStorage.getItem(STORAGE_KEYS.LOGIN_SESSION);

    // Don't refresh if login session expired
    if (loginSession && Date.now() > Number.parseInt(loginSession)) {
      return false;
    }

    if (!tokenExpiry) return false;

    const expiryTime = Number.parseInt(tokenExpiry);
    const refreshThreshold = expiryTime - TOKEN_REFRESH_BUFFER;

    return Date.now() >= refreshThreshold;
  } catch (error) {
    console.error("Error checking token refresh:", error);
    return false;
  }
};

/**
 * Check if token is valid (exists and not expired)
 */
export const isTokenValid = () => {
  return getToken() !== null;
};

/**
 * Check if login session (24h) is still active
 */
export const isLoginSessionActive = () => {
  try {
    const loginSession = localStorage.getItem(STORAGE_KEYS.LOGIN_SESSION);
    if (!loginSession) return false;
    return Date.now() < Number.parseInt(loginSession);
  } catch (error) {
    console.error("Error checking login session:", error);
    return false;
  }
};

// REFRESH TOKEN MANAGEMENT 

/**
 * Store refresh token
 */
export const setRefreshToken = (refreshToken) => {
  if (!refreshToken) return;
  try {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  } catch (error) {
    console.error("Error storing refresh token:", error);
  }
};

/**
 * Get refresh token
 */
export const getRefreshToken = () => {
  try {
    // Only return refresh token if login session is active
    const loginSession = localStorage.getItem(STORAGE_KEYS.LOGIN_SESSION);
    if (loginSession && Date.now() > Number.parseInt(loginSession)) {
      return null; // Login session expired
    }
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  } catch (error) {
    console.error("Error getting refresh token:", error);
    return null;
  }
};

// ROLE MANAGEMENT 

/**
 * Store user role securely
 * @param {string} role - User role (DCS, CHECKER, APPROVER, ADMIN, etc.)
 */
export const setRole = (role) => {
  if (!role) return;
  try {
    localStorage.setItem(STORAGE_KEYS.ROLE, role.toUpperCase());
  } catch (error) {
    console.error("Error storing role:", error);
  }
};

/**
 * Get user role from localStorage
 */
export const getRole = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.ROLE);
  } catch (error) {
    console.error("Error getting role:", error);
    return null;
  }
};

/**
 * Validate if current role is one of the allowed roles
 * @param {string[]} allowedRoles - Array of allowed roles
 */
export const hasRole = (allowedRoles) => {
  const currentRole = getRole();
  if (!currentRole) return false;

  return allowedRoles.map(r => r.toUpperCase()).includes(currentRole.toUpperCase());
};

export const setEntityType = (entityType) => {
  if (!entityType) return;
  try {
    localStorage.setItem(STORAGE_KEYS.ENTITY_TYPE, entityType.toUpperCase());
  } catch (error) {
    console.error("Error storing entity type:", error);
  }
};

export const getEntityType = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.ENTITY_TYPE);
  } catch (error) {
    console.error("Error getting entity type:", error);
    return null;
  }
};

//  PERMISSIONS MANAGEMENT 

/**
 * Store user permissions
 * @param {Array} permissions - Permissions array from API
 */
export const setPermissions = (permissions) => {
  if (!permissions) return;
  try {
    localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(permissions));
  } catch (error) {
    console.error("Error storing permissions:", error);
  }
};

/**
 * Get user permissions
 */
export const getPermissions = () => {
  try {
    const permissions = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
    return permissions ? JSON.parse(permissions) : [];
  } catch (error) {
    console.error("Error getting permissions:", error);
    return [];
  }
};

/**
 * Check if user has specific permission
 * @param {string} module - Module name (e.g., "FARMERMASTER")
 * @param {string} action - Action (e.g., "ADD", "EDIT", "DELETE")
 */
export const hasPermission = (module, action) => {
  const permissions = getPermissions();
  return permissions.some(
    (perm) => perm[1] === module && perm[2] === action
  );
};

export const hasAccessToProgram = (programCode) => {
  if (!programCode) return false;
  const permissions = getPermissions();
  if (permissions.some(p => p[1] === "*")) return true;
  return permissions.some((perm) => perm[1] === programCode);
};

export const getActionsForProgram = (programCode) => {
  const permissions = getPermissions();
  if (permissions.some(p => p[1] === "*" && p[2] === "*")) {
    return ["ADD", "EDIT", "DELETE", "EXPORT", "CHECK", "APPROVE", "REPORT"];
  }
  return permissions
    .filter(p => p[1] === programCode)
    .map(p => p[2]);
};

export const getAccessibleProgramCodes = () => {
  const permissions = getPermissions();
  if (permissions.some(p => p[1] === "*")) return ["*"];
  return [...new Set(permissions.map(p => p[1]))];
};

// USER DATA MANAGEMENT

/**
 * Store non-sensitive user data
 * @param {Object} userData - User data object
 */
export const setUserData = (userData) => {
  if (!userData) return;
  try {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
  } catch (error) {
    console.error("Error storing user data:", error);
  }
};

/**
 * Get user data
 */
export const getUserData = () => {
  try {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error("Error getting user data:", error);
    return null;
  }
};

// ============== CLEAR AUTH DATA ==============

/**
 * Clear all auth data from localStorage
 */
export const clearAuth = () => {
  try {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
    console.log("✅ [SecureStorage] All auth data cleared");
  } catch (error) {
    console.error("Error clearing auth data:", error);
  }
};

// INITIALIZE AUTH

/**
 * Initialize auth from localStorage
 * Returns auth state object
 */
export const getAuthState = () => {
  return {
    token: getToken(),
    refreshToken: getRefreshToken(),
    role: getRole(),
    entityType: getEntityType(),
    permissions: getPermissions(),
    userData: getUserData(),
    isAuthenticated: isTokenValid(),
  };
};

//DECODE JWT

/**
 * Decode JWT token to get payload (without verification)
 * @param {string} token - JWT token
 */
export const decodeToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replaceAll("-", "+").replaceAll("_", "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.codePointAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

/**
 * Get token expiry from JWT payload
 * @param {string} token - JWT token
 */
export const getTokenExpiryFromJWT = (token) => {
  const payload = decodeToken(token);
  if (!payload?.exp) return null;
  return payload.exp * 1000; // Convert to milliseconds
};

/**
 * Get role directly from JWT token (tamper-proof)
 */
export const getRoleFromJWT = () => {
  const token = getRawToken();
  if (!token) return null;

  const payload = decodeToken(token);
  return payload?.role || null;
};
/**
 * Get username directly from JWT token 
 */
export const getUserNameFromJWT = () => {
  // Use raw token to avoid expiry check issues
  const token = getRawToken();
  if (!token) return null;

  const payload = decodeToken(token);
  return payload?.userName || payload?.name || payload?.username || null;
};

/**
 * Get entity_id from JWT token
 */
export const getEntityIdFromJWT = () => {
  const token = getRawToken();
  if (!token) return null;

  const payload = decodeToken(token);
  return payload?.entity_id || null;
};

/**
 * Get phone number from JWT token
 */
export const getPhoneFromJWT = () => {
  const token = getRawToken();
  if (!token) return null;

  const payload = decodeToken(token);
  return payload?.phone || null;
};

/**
 * Get entity_type from JWT token
 */
export const getEntityTypeFromJWT = () => {
  const token = getRawToken();
  if (!token) return null;

  const payload = decodeToken(token);
  return payload?.entity_type || null;
};

export default {
  setToken,
  getToken,
  getRawToken,
  setRefreshToken,
  getRefreshToken,
  setRole,
  getRole,
  hasRole,
  setPermissions,
  getPermissions,
  hasPermission,
  setUserData,
  getUserData,
  clearAuth,
  getAuthState,
  isTokenValid,
  isLoginSessionActive,
  shouldRefreshToken,
  decodeToken,
  getTokenExpiryFromJWT,
  getRoleFromJWT,
  getUserNameFromJWT,
  getEntityIdFromJWT,
  getPhoneFromJWT,
  getEntityTypeFromJWT,
};
